import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';

import BusyOverlay from '@bidding/shared/components/BusyOverlay/BusyOverlay';
import useAuth from '../hooks/useAuth';
import { extractErrorMessage } from '../helpers/errorMessage';
import GoogleSignInButton, { useGoogleAuthAvailable } from './GoogleSignInButton';

function validate(values) {
    const errors = {};
    if (!values.email) errors.email = 'Please enter your email.';
    else if (!/.+@.+\..+/.test(values.email)) errors.email = 'That doesn’t look like a valid email.';
    if (!values.password) errors.password = 'Please enter your password.';
    return errors;
}

function IconInput({ name, type = 'text', placeholder, icon, trailing, required }) {
    return (
        <Field name={name}>
            {({ input, meta }) => {
                const showError = meta.touched && meta.error;
                return (
                    <>
                        <span className={`bidding-auth2-input${showError ? ' has-error' : ''}`}>
                            {icon && (
                                <span className="bidding-auth2-input__ico" aria-hidden="true">
                                    {icon}
                                </span>
                            )}
                            <input
                                {...input}
                                type={type}
                                placeholder={placeholder}
                                autoComplete={type === 'password' ? 'current-password' : type === 'email' ? 'email' : undefined}
                                aria-required={required || undefined}
                            />
                            {trailing}
                        </span>
                        {showError && (
                            <span className="bidding-auth2-field__error">{meta.error}</span>
                        )}
                    </>
                );
            }}
        </Field>
    );
}

function EyeButton({ shown, onToggle }) {
    return (
        <button
            type="button"
            className="bidding-auth2-input__eye"
            onClick={onToggle}
            aria-label={shown ? 'Hide password' : 'Show password'}
            tabIndex={-1}
        >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {shown
                    ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><path d="M1 1l22 22"/></>
                    : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
            </svg>
        </button>
    );
}

const EmailIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
);

const KeyIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m21 2-9.6 9.6"/><circle cx="7.5" cy="15.5" r="5.5"/><path d="m15 6 3 3"/><path d="m14 7 3 3"/></svg>
);

export default function LoginForm({ onForgot, onRegister, onSuccess, onBusy, busyMessage }) {
    const { submitLogin } = useAuth();
    const googleAvailable = useGoogleAuthAvailable();
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const onSubmit = async (values) => {
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        if (onBusy) onBusy(busyMessage || 'Signing you in…');
        const minDelay = new Promise((resolve) => setTimeout(resolve, 600));
        try {
            await Promise.all([submitLogin(values), minDelay]);
            if (onSuccess) onSuccess();
        } catch (e) {
            setError(extractErrorMessage(e));
            if (onBusy) onBusy(null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="bidding-auth2-card">
            <span className="bidding-auth2-card__kicker">Sign in</span>
            <h2 className="bidding-auth2-card__title">Welcome back</h2>
            <p className="bidding-auth2-card__subtitle">
                Log in to pick up where you left off: orders, writers, balance,
                everything in one place.
            </p>

            <Form onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="bidding-auth2-form">
                        <label className="bidding-auth2-field">
                            <span className="bidding-auth2-field__label">Email</span>
                            <IconInput
                                name="email"
                                type="email"
                                placeholder="you@example.com"
                                icon={EmailIcon}
                                required
                            />
                        </label>

                        <label className="bidding-auth2-field">
                            <span className="bidding-auth2-field__label">
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); onForgot(); }}
                                    tabIndex={-1}
                                >
                                    Forgot password?
                                </a>
                                Password
                            </span>
                            <IconInput
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                icon={KeyIcon}
                                required
                                trailing={
                                    <EyeButton
                                        shown={showPassword}
                                        onToggle={() => setShowPassword(v => !v)}
                                    />
                                }
                            />
                        </label>

                        <button
                            type="submit"
                            className="bidding-auth2-submit"
                            disabled={submitting}
                        >
                            {submitting ? 'Signing in…' : 'Log in'}
                        </button>

                        <div className="bidding-auth2-error-slot" aria-live="polite">
                            {error && (
                                <div className="bidding-auth2-error">{error}</div>
                            )}
                        </div>

                        {googleAvailable && (
                            <>
                                <div className="bidding-auth2-divider">or</div>
                                <GoogleSignInButton
                                    label="Continue with Google"
                                    className="bidding-auth2-google"
                                    onSuccess={onSuccess}
                                />
                            </>
                        )}
                    </form>
                )}
            </Form>

            <div className="bidding-auth2-card__foot">
                Don&rsquo;t have an account?{' '}
                <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onRegister(); }}
                >
                    Sign up
                </a>
            </div>

            {submitting && <BusyOverlay message="Signing you in" />}
        </section>
    );
}
